import React, { useEffect, useState } from 'react';
import {
    Card, CardContent, Divider,
    Table, TableBody, TableCell,
    TableContainer, TableHead, TableRow,
    TextField, Stack, CardHeader,
    Checkbox, Menu, MenuItem, Button,
    Dialog, DialogTitle, DialogContent, DialogActions, Typography, IconButton
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import AddIcon from '@mui/icons-material/Add';
import SearchIcon from '@mui/icons-material/Search';
import { api } from '../../../services/Main/Api';
import PaginationPage from '../../../components/PaginationPage/PaginationPage';
import { useSnackbar } from 'notistack';
import { getCondoId } from '../../../services/Main/AuthStorage';

const CategoryFee = () => {
    const [search, setSearch] = useState('');
    const [items, setItems] = useState([]);
    const [selectedIds, setSelectedIds] = useState([]);
    const [loading, setLoading] = useState(true);
    const [anchorEl, setAnchorEl] = useState(null);
    const [confirmOpen, setConfirmOpen] = useState(false);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [editMode, setEditMode] = useState(false);
    const [editId, setEditId] = useState(null);
    const [page, setPage] = useState(1);
    const [showSearch, setShowSearch] = useState(false);
    const [errors, setErrors] = useState({});
    const { enqueueSnackbar } = useSnackbar();

    const [formData, setFormData] = useState({ title: '', company: getCondoId() });
    const openMenu = Boolean(anchorEl);

    useEffect(() => {
        const filters = { search, page, page_size: 50, company: getCondoId() };
        api.get('/category-fee/', { params: filters })
            .then(response => {
                setItems(response.data);
                window.scrollTo({ top: 0, behavior: 'smooth' });
                setSelectedIds([]);
            })
            .catch(console.error)
            .finally(() => setLoading(false));
    }, [search, page]);

    const handleSelectAll = (event) => {
        const results = items?.results || [];
        setSelectedIds(event.target.checked ? results.map((u) => u.id) : []);
    };

    const handleSelectOne = (id) => {
        setSelectedIds((prev) => prev.includes(id) ? prev.filter((sid) => sid !== id) : [...prev, id]);
    };

    const handleMenuClick = (event) => setAnchorEl(event.currentTarget);
    const handleMenuClose = () => setAnchorEl(null);
    const handleDeleteClick = () => { setConfirmOpen(true); handleMenuClose(); };

    const handleDeleteConfirmed = async () => {
        try {
            await Promise.all(
                selectedIds.map((id) => api.delete(`/category-fee/${id}/`))
            );
            const updated = items.results.filter((u) => !selectedIds.includes(u.id));
            setItems((prev) => ({ ...prev, results: updated }));
            enqueueSnackbar("Categoria deletada com sucesso!", { variant: 'success' });
        } catch (error) {
            console.error(error);
            enqueueSnackbar("Erro ao deletar categorias.", { variant: 'error' });
        } finally {
            setSelectedIds([]);
            setConfirmOpen(false);
        }
    };

    const handleRowClick = (item) => {
        setFormData({ title: item.title, company: item.company });
        setEditId(item.id);
        setEditMode(true);
        setDialogOpen(true);
    };

    const handleDialogChange = (e) => {
        const { name, value } = e.target;
        setFormData((prev) => ({ ...prev, [name]: value }));
    };

    const validateFields = () => {
        const newErrors = {};
        if (!formData.title?.trim()) newErrors.title = 'Campo obrigatório';
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const generateTag = (title) => {
        return title.toUpperCase().replace(/\s+/g, '-');
    };

    const handleSave = () => {
        if (!validateFields()) return;
        const payload = {
            ...formData,
            company: getCondoId(),
            tag: generateTag(formData.title)
        };
        const clearForm = () => {
            setFormData({ title: '', company: getCondoId() });
            setErrors({});
        };
        if (editMode) {
            api.put(`/category-fee/${editId}/`, payload)
                .then(response => {
                    const updated = items.results.map(u => u.id === editId ? response.data : u);
                    setItems({ ...items, results: updated });
                    setDialogOpen(false);
                    clearForm();
                    setEditMode(false);
                    enqueueSnackbar("Categoria atualizada com sucesso!", { variant: 'success' });
                });
        } else {
            api.post('/category-fee/', payload)
                .then((response) => {
                    setItems((prev) => ({ ...prev, results: [...(prev.results || []), response.data] }));
                    setDialogOpen(false);
                    clearForm();
                    enqueueSnackbar("Categoria criada com sucesso!", { variant: 'success' });
                });
        }
    };

    const isSelected = (id) => selectedIds.includes(id);
    const allSelected = items.results && items.results.length > 0 && selectedIds.length === items.results.length;

    return (
        <>
            <Card sx={{ margin: 2 }}>
                <CardHeader title={`Categorias (${items?.count || 0})`} />
                <Divider sx={{ mb: 2 }} />
                <Stack direction="row" sx={{ px: 2 }} spacing={2} alignItems="center">
                    <Button variant="contained" size="small" startIcon={<AddIcon />} onClick={() => {
                        setDialogOpen(true);
                        setEditMode(false);
                        setFormData({ title: '', company: getCondoId() });
                    }}>Nova Categoria</Button>
                    {showSearch ? (
                        <TextField size="small" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Pesquisar..."
                                   autoFocus onBlur={() => { if (!search.trim()) setShowSearch(false); }}
                                   InputProps={{ startAdornment: <SearchIcon fontSize="small" sx={{ mr: 1 }} /> }}
                        />
                    ) : (
                        <IconButton onClick={() => setShowSearch(true)}><SearchIcon /></IconButton>
                    )}
                    <Button variant="outlined" size="small" startIcon={<KeyboardArrowDownIcon />} onClick={handleMenuClick}
                            disabled={selectedIds.length === 0}>
                        Ações {selectedIds.length > 0 ? `(${selectedIds.length})` : ''}
                    </Button>
                    <Menu anchorEl={anchorEl} open={openMenu} onClose={handleMenuClose}>
                        <MenuItem onClick={handleDeleteClick}><DeleteIcon fontSize="small" sx={{ mr: 1 }} />Excluir selecionados</MenuItem>
                    </Menu>
                </Stack>
                <CardContent>
                    {loading ? (
                        <Typography variant="body2">Carregando categorias...</Typography>
                    ) : (
                        <TableContainer>
                            <Table>
                                <TableHead>
                                    <TableRow>
                                        <TableCell padding="checkbox">
                                            <Checkbox checked={allSelected} onChange={handleSelectAll} />
                                        </TableCell>
                                        <TableCell><strong>Título</strong></TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {items?.results?.map((item) => (
                                        <TableRow key={item.id} hover sx={{ cursor: 'pointer' }} onClick={() => handleRowClick(item)}>
                                            <TableCell padding="checkbox" onClick={(e) => e.stopPropagation()}>
                                                <Checkbox checked={isSelected(item.id)} onChange={() => handleSelectOne(item.id)} />
                                            </TableCell>
                                            <TableCell>{item.title}</TableCell>
                                        </TableRow>
                                    ))}
                                    {items.results?.length === 0 && (
                                        <TableRow><TableCell colSpan={2} align="center">Nenhuma categoria encontrada.</TableCell></TableRow>
                                    )}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    )}
                </CardContent>
            </Card>
            <PaginationPage data={{ ...items, page }} onChange={setPage} />
            <Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)}>
                <DialogTitle>Confirmar Exclusão</DialogTitle>
                <DialogContent>
                    <Typography>Deseja excluir {selectedIds.length} {selectedIds.length === 1 ? 'categoria' : 'categorias'}?</Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setConfirmOpen(false)} size="small">Cancelar</Button>
                    <Button onClick={handleDeleteConfirmed} color="error" variant="contained" size="small">Deletar</Button>
                </DialogActions>
            </Dialog>
            <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} fullWidth maxWidth="sm">
                <DialogTitle>{editMode ? 'Editar Categoria' : 'Nova Categoria'}</DialogTitle>
                <Divider />
                <DialogContent>
                    <TextField label="Título" name="title" value={formData.title || ''} onChange={handleDialogChange} fullWidth size="small"
                               error={!!errors.title} helperText={errors.title} sx={{ mt: 2 }} />
                </DialogContent>
                <Divider />
                <DialogActions sx={{ py: 3, display: 'flex', justifyContent: 'space-between' }}>
                    <Button onClick={() => setDialogOpen(false)}>Cancelar</Button>
                    <Button onClick={handleSave} variant="contained">Salvar</Button>
                </DialogActions>
            </Dialog>
        </>
    );
};

export default CategoryFee;
